/**
 * Sponsor self-serve portal.
 *
 * The point of this screen is that nobody at FairwayLive has to touch a booking:
 * a sponsor picks a package, pays through the gateway, uploads creative, and it
 * starts serving once approved. Delivery and spend are live on the same page.
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatCents } from '@fairwaylive/shared';
import { ErrorNote, Section, Spinner } from '../components/Shell.tsx';
import { getToken, post } from '../lib/api.ts';
import { useFetch } from '../lib/useLive.ts';

interface Me {
  user: { id: string; email: string };
  organizations: Array<{ organizationId: string; name: string; type: string; role: string; plan: { key: string; name: string } }>;
}

interface RateCard {
  currency: string;
  zones: Array<{ key: string; label: string; kind: string; currentBuyers: number; size: { w: number; h: number } }>;
  packages: Array<{
    id: string;
    key: string;
    name: string;
    zoneKey: string;
    pricingModel: string;
    flatPriceCents: number | null;
    cpmCents: number | null;
    impressionsIncluded: number | null;
    durationDays: number;
    isExclusive: boolean;
    soldOut: boolean;
    priceDisplay: string;
    description: string | null;
  }>;
}

interface Analytics {
  totals: { impressions: number; clicks: number; ctr: number; spendCents: number; spendDisplay: string };
  campaigns: Array<{
    placementId: string;
    creativeName: string;
    zoneLabel: string;
    impressions: number;
    clicks: number;
    ctr: number;
    deliveryPercent: number | null;
    spendDisplay: string;
    effectiveCpmCents: number | null;
    isPaused: boolean;
  }>;
  analyticsTier: string;
  upsell?: { addOnKey: string; message: string };
}

export function SponsorPortalPage() {
  const token = getToken();
  const me = useFetch<Me>(token ? '/api/auth/me' : null);

  if (!token) {
    return (
      <Section eyebrow="Sponsor portal" title="Sign in to buy and manage placements">
        <div className="card max-w-[560px] p-6">
          <p className="text-sm text-[var(--color-muted)]">
            Sponsor accounts get the rate card with live availability, self-serve checkout, creative upload with
            moderation status, and delivery reporting per placement.
          </p>
          <div className="mt-5 flex gap-3">
            <Link to="/login?intent=sponsor" className="btn btn-primary">
              Sign in
            </Link>
            <Link to="/login?intent=signup&type=advertiser" className="btn btn-ghost">
              Create a sponsor account
            </Link>
          </div>
          <p className="mt-4 text-xs text-[var(--color-muted-dim)]">
            Browsing first? The{' '}
            <Link to="/rate-card" className="text-[var(--color-fairway-300)] underline">
              public rate card
            </Link>{' '}
            shows every placement, its price and a live preview.
          </p>
        </div>
      </Section>
    );
  }

  if (me.loading) return <Spinner label="Loading your account" />;
  if (me.error || !me.data) {
    return (
      <Section title="Sponsor portal">
        <ErrorNote message={me.error ?? 'Could not load your account'} hint="Your session may have expired — sign in again." />
      </Section>
    );
  }

  const advertiserOrg = me.data.organizations.find((o) => o.type === 'advertiser') ?? me.data.organizations[0];
  if (!advertiserOrg) {
    return (
      <Section title="Sponsor portal">
        <ErrorNote message="This login has no organization attached." hint="Create a sponsor account to buy inventory." />
      </Section>
    );
  }

  return <PortalBody orgId={advertiserOrg.organizationId} orgName={advertiserOrg.name} email={me.data.user.email} />;
}

function PortalBody({ orgId, orgName, email }: { orgId: string; orgName: string; email: string }) {
  const rateCard = useFetch<RateCard>(`/api/sponsor/${orgId}/rate-card`);
  const orders = useFetch<{ orders: Array<Record<string, unknown>> }>(`/api/sponsor/${orgId}/orders`);
  const creatives = useFetch<{ creatives: Array<Record<string, unknown>> }>(`/api/sponsor/${orgId}/creatives`);
  const analytics = useFetch<Analytics>(`/api/sponsor/${orgId}/analytics`);
  const [tab, setTab] = useState<'buy' | 'creative' | 'orders' | 'performance'>('buy');
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const tabs = [
    ['buy', 'Buy inventory'],
    ['creative', 'Creative'],
    ['orders', 'Orders'],
    ['performance', 'Performance'],
  ] as const;

  return (
    <div>
      <Section eyebrow={`Sponsor portal · ${orgName}`} title="Your inventory, spend and delivery">
        <div className="mb-6 flex flex-wrap gap-2">
          {tabs.map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={`btn !py-1.5 !text-[13px] ${tab === key ? 'btn-primary' : 'btn-ghost'}`}
            >
              {label}
            </button>
          ))}
        </div>

        {notice ? (
          <div className="card mb-5 border-[var(--color-fairway-300)] p-4 text-sm text-[var(--color-fairway-300)]">{notice}</div>
        ) : null}
        {error ? (
          <div className="mb-5">
            <ErrorNote message={error} />
          </div>
        ) : null}

        {tab === 'buy' ? (
          <BuyTab orgId={orgId} rateCard={rateCard.data} loading={rateCard.loading} onError={setError} />
        ) : null}

        {tab === 'creative' ? (
          <CreativeTab
            orgId={orgId}
            creatives={creatives.data?.creatives ?? []}
            orders={orders.data?.orders ?? []}
            onDone={(message) => {
              setNotice(message);
              setError(null);
              creatives.reload();
            }}
            onError={setError}
          />
        ) : null}

        {tab === 'orders' ? <OrdersTab orders={orders.data?.orders ?? []} loading={orders.loading} /> : null}

        {tab === 'performance' ? <PerformanceTab analytics={analytics.data} loading={analytics.loading} orgId={orgId} /> : null}
      </Section>

      <Section>
        <p className="text-xs text-[var(--color-muted-dim)]">
          Signed in as {email}. Payments run through the configured gateway — in development that is the mock gateway,
          which simulates success and failure without taking money.
        </p>
      </Section>
    </div>
  );
}

function BuyTab({
  orgId,
  rateCard,
  loading,
  onError,
}: {
  orgId: string;
  rateCard: RateCard | null;
  loading: boolean;
  onError: (message: string) => void;
}) {
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [startsOn, setStartsOn] = useState(new Date().toISOString().slice(0, 10));

  if (loading) return <Spinner label="Loading availability" />;
  if (!rateCard) return <ErrorNote message="Rate card unavailable for this account." />;

  const buy = async (packageKey: string) => {
    setBusyKey(packageKey);
    try {
      const result = await post<{ payment: { redirectUrl: string } }>(`/api/sponsor/${orgId}/orders`, {
        packageKey,
        startsOn,
      });
      // Straight to the gateway; fulfilment happens on the webhook.
      window.location.href = result.payment.redirectUrl;
    } catch (err) {
      onError((err as Error).message);
      setBusyKey(null);
    }
  };

  return (
    <div>
      <label className="mb-5 flex flex-wrap items-center gap-3 text-sm text-[var(--color-muted)]">
        Flight starts
        <input
          type="date"
          value={startsOn}
          onChange={(event) => setStartsOn(event.target.value)}
          className="rounded-lg border border-[color-mix(in_oklab,var(--color-fairway-400)_30%,transparent)] bg-[var(--color-fairway-800)] px-3 py-1.5 text-[var(--color-chalk)]"
        />
      </label>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {rateCard.packages.map((pkg) => {
          const zone = rateCard.zones.find((z) => z.key === pkg.zoneKey);
          return (
            <article key={pkg.key} className="card flex flex-col p-5">
              <div className="flex items-start justify-between gap-2">
                <h3 className="text-[15px] font-semibold">{pkg.name}</h3>
                {pkg.isExclusive ? (
                  <span className="whitespace-nowrap rounded bg-[var(--color-gold-500)] px-2 py-0.5 text-[10px] font-bold uppercase text-[#1a1204]">
                    Exclusive
                  </span>
                ) : null}
              </div>
              <p className="tabular mt-2 text-lg font-bold text-[var(--color-gold-400)]">{pkg.priceDisplay}</p>
              <p className="mt-1 text-[11px] text-[var(--color-muted-dim)]">
                {pkg.durationDays} days · {zone ? `${zone.size.w}×${zone.size.h}` : pkg.zoneKey} · {zone?.currentBuyers ?? 0}{' '}
                sponsor{(zone?.currentBuyers ?? 0) === 1 ? '' : 's'} currently in this zone
              </p>
              <p className="mt-3 flex-1 text-[13px] text-[var(--color-muted)]">{pkg.description}</p>
              <button
                type="button"
                disabled={pkg.soldOut || busyKey === pkg.key}
                onClick={() => void buy(pkg.key)}
                className={`btn mt-4 w-full ${pkg.soldOut ? 'btn-ghost opacity-50' : 'btn-primary'}`}
              >
                {pkg.soldOut ? 'Sold out for these dates' : busyKey === pkg.key ? 'Opening checkout…' : 'Buy and pay'}
              </button>
            </article>
          );
        })}
      </div>
      {!rateCard.packages.length ? (
        <ErrorNote message="No packages are configured yet." hint="Run npm run db:seed to load the rate card." />
      ) : null}
    </div>
  );
}

function CreativeTab({
  orgId,
  creatives,
  orders,
  onDone,
  onError,
}: {
  orgId: string;
  creatives: Array<Record<string, unknown>>;
  orders: Array<Record<string, unknown>>;
  onDone: (message: string) => void;
  onError: (message: string) => void;
}) {
  const [name, setName] = useState('');
  const [clickUrl, setClickUrl] = useState('');
  const [altText, setAltText] = useState('');
  const [dataUri, setDataUri] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const payableOrders = useMemo(() => orders.filter((o) => o.status === 'scheduled' || o.status === 'live'), [orders]);

  const onFile = (file: File | undefined) => {
    if (!file) return;
    if (file.size > 2_000_000) {
      onError('Keep creative under 2 MB. For video, host it and paste the URL instead.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setDataUri(String(reader.result));
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    if (!name || !dataUri) {
      onError('A name and an image are both required.');
      return;
    }
    setBusy(true);
    try {
      await post(`/api/sponsor/${orgId}/creatives`, {
        name,
        kind: 'image',
        assetUrl: dataUri,
        clickUrl: clickUrl || undefined,
        altText: altText || name,
      });
      setName('');
      setClickUrl('');
      setAltText('');
      setDataUri(null);
      onDone('Creative uploaded. Once approved, attach it to a paid order to start serving.');
    } catch (err) {
      onError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const attach = async (creativeId: string, orderId: string) => {
    try {
      await post(`/api/sponsor/${orgId}/placements`, { creativeId, orderId });
      onDone('Placement scheduled. It serves inside the order flight dates.');
    } catch (err) {
      onError((err as Error).message);
    }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[380px_minmax(0,1fr)]">
      <div className="card p-5">
        <h3 className="board-title mb-4 text-lg font-bold">Upload creative</h3>
        <div className="space-y-3">
          <Field label="Name">
            <input value={name} onChange={(e) => setName(e.target.value)} className={inputClass} placeholder="Winter campaign — banner" />
          </Field>
          <Field label="Image (PNG, JPG or SVG, max 2 MB)">
            <input type="file" accept="image/*" onChange={(e) => onFile(e.target.files?.[0])} className="text-xs text-[var(--color-muted)]" />
          </Field>
          <Field label="Click-through URL">
            <input value={clickUrl} onChange={(e) => setClickUrl(e.target.value)} className={inputClass} placeholder="https://example.com/offer" />
          </Field>
          <Field label="Alt text (accessibility)">
            <input value={altText} onChange={(e) => setAltText(e.target.value)} className={inputClass} placeholder="Kalahari Motors winter service special" />
          </Field>
          {dataUri ? <img src={dataUri} alt="Creative preview" className="w-full rounded-lg border border-[color-mix(in_oklab,var(--color-fairway-400)_25%,transparent)]" /> : null}
          <button type="button" onClick={() => void submit()} disabled={busy} className="btn btn-primary w-full">
            {busy ? 'Uploading…' : 'Submit for approval'}
          </button>
        </div>
      </div>

      <div>
        <h3 className="board-title mb-3 text-lg font-bold">Your creatives</h3>
        {!creatives.length ? (
          <p className="text-sm text-[var(--color-muted)]">Nothing uploaded yet.</p>
        ) : (
          <div className="space-y-3">
            {creatives.map((creative) => {
              const status = String(creative.moderationStatus);
              return (
                <div key={String(creative.id)} className="card flex flex-wrap items-center gap-4 p-4">
                  {creative.assetUrl ? (
                    <img src={String(creative.assetUrl)} alt="" className="h-14 w-24 rounded object-cover" />
                  ) : null}
                  <div className="min-w-[160px] flex-1">
                    <p className="text-sm font-semibold">{String(creative.name)}</p>
                    <p className="text-[11px] text-[var(--color-muted-dim)]">
                      {String(creative.widthPx ?? '?')}×{String(creative.heightPx ?? '?')} ·{' '}
                      <span
                        className={
                          status === 'approved'
                            ? 'text-[var(--color-fairway-300)]'
                            : status === 'rejected'
                              ? 'text-[var(--color-rose-sun)]'
                              : 'text-[var(--color-gold-400)]'
                        }
                      >
                        {status}
                      </span>
                    </p>
                  </div>
                  {status === 'approved' && payableOrders.length ? (
                    <select
                      defaultValue=""
                      onChange={(event) => {
                        if (event.target.value) void attach(String(creative.id), event.target.value);
                      }}
                      className={inputClass}
                    >
                      <option value="">Attach to order…</option>
                      {payableOrders.map((order) => (
                        <option key={String(order.id)} value={String(order.id)}>
                          {String(order.zoneKey)} · {String(order.startsOn)} → {String(order.endsOn)}
                        </option>
                      ))}
                    </select>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function OrdersTab({ orders, loading }: { orders: Array<Record<string, unknown>>; loading: boolean }) {
  if (loading) return <Spinner label="Loading orders" />;
  if (!orders.length) return <p className="text-sm text-[var(--color-muted)]">No orders yet. Buy a placement to get started.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[720px] border-collapse text-sm">
        <thead>
          <tr className="border-b border-[color-mix(in_oklab,var(--color-fairway-400)_26%,transparent)] text-left text-[var(--color-muted)]">
            <th className="py-2">Zone</th>
            <th className="py-2">Flight</th>
            <th className="py-2">Model</th>
            <th className="py-2 text-right">Value</th>
            <th className="py-2">Status</th>
            <th className="py-2">Payment</th>
            <th className="py-2 text-right">Creatives</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((order) => (
            <tr key={String(order.id)} className="border-b border-[color-mix(in_oklab,var(--color-fairway-400)_10%,transparent)]">
              <td className="py-2 font-medium">{String(order.zoneKey)}</td>
              <td className="tabular py-2 text-[var(--color-muted)]">
                {String(order.startsOn)} → {String(order.endsOn)}
              </td>
              <td className="py-2 uppercase text-[11px] text-[var(--color-muted)]">{String(order.pricingModel)}</td>
              <td className="tabular py-2 text-right">{String(order.amountDisplay ?? '')}</td>
              <td className="py-2">
                <StatusPill status={String(order.status)} />
              </td>
              <td className="py-2 text-[12px] text-[var(--color-muted)]">{String(order.paymentStatus ?? '—')}</td>
              <td className="tabular py-2 text-right">{String(order.placementCount ?? 0)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PerformanceTab({ analytics, loading, orgId }: { analytics: Analytics | null; loading: boolean; orgId: string }) {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  if (loading) return <Spinner label="Loading delivery" />;
  if (!analytics) return <ErrorNote message="No analytics available." />;

  const buyAddOn = async () => {
    setBusy(true);
    try {
      const result = await post<{ payment: { redirectUrl: string } }>(`/api/orgs/${orgId}/billing/add-ons`, {
        addOnKey: 'analytics_pro',
      });
      window.location.href = result.payment.redirectUrl;
    } catch (err) {
      setMessage((err as Error).message);
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="mb-6 grid gap-4 sm:grid-cols-4">
        <Stat label="Impressions" value={analytics.totals.impressions.toLocaleString('en-ZA')} />
        <Stat label="Clicks" value={analytics.totals.clicks.toLocaleString('en-ZA')} />
        <Stat label="CTR" value={`${analytics.totals.ctr}%`} />
        <Stat label="Spend" value={analytics.totals.spendDisplay} />
      </div>

      {analytics.campaigns.length ? (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-[color-mix(in_oklab,var(--color-fairway-400)_26%,transparent)] text-left text-[var(--color-muted)]">
                <th className="py-2">Creative</th>
                <th className="py-2">Placement</th>
                <th className="py-2 text-right">Impressions</th>
                <th className="py-2 text-right">Clicks</th>
                <th className="py-2 text-right">CTR</th>
                <th className="py-2 text-right">Delivery</th>
                <th className="py-2 text-right">Spend</th>
                <th className="py-2 text-right">eCPM</th>
              </tr>
            </thead>
            <tbody>
              {analytics.campaigns.map((campaign) => (
                <tr key={campaign.placementId} className="border-b border-[color-mix(in_oklab,var(--color-fairway-400)_10%,transparent)]">
                  <td className="py-2 font-medium">
                    {campaign.creativeName}
                    {campaign.isPaused ? <span className="ml-2 text-[10px] uppercase text-[var(--color-gold-400)]">paused</span> : null}
                  </td>
                  <td className="py-2 text-[var(--color-muted)]">{campaign.zoneLabel}</td>
                  <td className="tabular py-2 text-right">{campaign.impressions.toLocaleString('en-ZA')}</td>
                  <td className="tabular py-2 text-right">{campaign.clicks.toLocaleString('en-ZA')}</td>
                  <td className="tabular py-2 text-right">{campaign.ctr}%</td>
                  <td className="tabular py-2 text-right">
                    {campaign.deliveryPercent === null ? '—' : `${campaign.deliveryPercent}%`}
                  </td>
                  <td className="tabular py-2 text-right">{campaign.spendDisplay}</td>
                  <td className="tabular py-2 text-right text-[var(--color-muted)]">
                    {campaign.effectiveCpmCents === null ? '—' : formatCents(campaign.effectiveCpmCents)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-sm text-[var(--color-muted)]">No delivery yet — placements report as soon as they serve.</p>
      )}

      {analytics.analyticsTier === 'basic' && analytics.upsell ? (
        <div className="card mt-6 flex flex-wrap items-center justify-between gap-4 border-[var(--color-gold-500)] p-5">
          <div>
            <p className="eyebrow text-[var(--color-gold-400)]">Analytics Pro</p>
            <p className="mt-1 max-w-[56ch] text-sm text-[var(--color-muted)]">{analytics.upsell.message}</p>
            {message ? <p className="mt-2 text-xs text-[var(--color-rose-sun)]">{message}</p> : null}
          </div>
          <button type="button" onClick={() => void buyAddOn()} disabled={busy} className="btn btn-gold">
            {busy ? 'Opening checkout…' : 'Add it'}
          </button>
        </div>
      ) : null}
    </div>
  );
}

const inputClass =
  'w-full rounded-lg border border-[color-mix(in_oklab,var(--color-fairway-400)_30%,transparent)] bg-[var(--color-fairway-800)] px-3 py-2 text-sm text-[var(--color-chalk)] placeholder:text-[var(--color-muted-dim)]';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] uppercase tracking-wider text-[var(--color-muted)]">{label}</span>
      {children}
    </label>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="card p-4">
      <p className="eyebrow">{label}</p>
      <p className="tabular mt-1 text-2xl font-bold text-[var(--color-fairway-300)]">{value}</p>
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const tone =
    status === 'live'
      ? 'bg-[var(--color-fairway-300)] text-[var(--color-fairway-950)]'
      : status === 'awaiting_payment'
        ? 'bg-[var(--color-gold-500)] text-[#1a1204]'
        : status === 'cancelled'
          ? 'bg-[color-mix(in_oklab,var(--color-rose-sun)_30%,transparent)] text-[var(--color-rose-sun)]'
          : 'bg-[var(--color-fairway-700)] text-[var(--color-muted)]';
  return <span className={`rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${tone}`}>{status.replace(/_/g, ' ')}</span>;
}
